/**
 * A window added by a plugin (`plugin:<id>:<window>`): the content the plugin describes, drawn
 * by {@link UiPart}. It asks for its content when it opens, again when the plugin says so, and
 * shows the plugin's answer to each button (with every field's value).
 *
 * Whatever the plugin answers, it can only use these pieces: it cannot run code in the page.
 */
import { Show, createEffect, onCleanup, onMount, type JSX } from "solid-js";
import { createStore, reconcile } from "solid-js/store";
import type { UiNode } from "@protocol/UiNode";
import { useApp } from "../../app/context";
import type { UiContext } from "./context";
import { UiPart } from "./UiView";
import { fieldValues } from "./values";

/** The window of a plugin, named `window`. */
export function PluginWindow(props: { window: string }): JSX.Element {
  const { pluginViews, graph, view } = useApp();
  const content = () => pluginViews.content(props.window);
  const [values, setValues] = createStore<Record<string, string>>({});

  onMount(() => void pluginViews.load(props.window));
  onCleanup(() => pluginViews.release(props.window));
  // New content: the fields start from what the plugin says.
  createEffect(() => {
    const v = content().view;
    if (v) setValues(reconcile(fieldValues(v)));
  });

  const ctx: UiContext = {
    value: (name) => values[name] ?? "",
    setValue: (name, value) => setValues(name, value),
    press: (action) => void pluginViews.act(props.window, action, { ...values }),
    openNode(id) {
      view.selectNode(id);
      view.requestCenter(id);
    },
    nodeName: (id) => graph.state.graph.nodes.find((n) => n.id === id)?.name,
    busy: () => content().busy,
    render: (node: UiNode) => <UiPart node={node} ctx={ctx} />,
  };

  return (
    <div class="pluginview" data-plugin-window={props.window} aria-busy={content().busy}>
      <div class="pluginview-bar">
        <button type="button" class="mini" data-plugin-refresh={props.window} disabled={content().busy} onClick={() => void pluginViews.load(props.window)}>
          Refresh
        </button>
        <Show when={content().busy}>
          <span class="small muted">working…</span>
        </Show>
      </div>
      <Show when={content().error}>
        <p class="small err" data-plugin-error={props.window}>
          {content().error}
        </p>
      </Show>
      <Show when={content().view} fallback={<Show when={!content().error}><p class="small muted">Loading…</p></Show>}>
        {(v) => <UiPart node={v()} ctx={ctx} />}
      </Show>
    </div>
  );
}
